// Extends the theme's own <quantity-input> (assets/global.js) so a single "+/-" stepper can
// silently swap which product VARIANT gets added to cart as the seat count crosses a tier
// boundary (e.g. additional seats 4 -> 5, Tier A -> Tier B), instead of one variant covering a
// wide quantity range. This exists because Cart Transform functions cannot reprice/split a line
// that carries a selling plan (superdocs-seat-pricing-spec.md s5), so Tier A and Tier B have to
// be two real variants and the swap has to happen client-side, before add-to-cart.
//
// Deliberately reuses <quantity-input>'s native stepUp()/stepDown() and disabled-button logic
// rather than re-implementing it -- the DOM's min/max stay statically at the union range across
// all tiers (see main-product.liquid's enable_seat_stepper block) so stepping always reaches the
// next value; only the underlying variant id and displayed price change on a tier crossing.
//
// Sets input[name="id"] on the associated product form and dispatches a bubbling `change` event
// on it -- the same signal assets/product-info.js's updateVariantInputs() uses for a native
// variant swap, and exactly what assets/seat-total.js already listens for (via its
// `this.productInfo?.addEventListener('change', this.update)`), so seat-total's price/renewal
// display recalculates for free with no changes to that file.
if (!customElements.get('seat-stepper')) {
  customElements.define(
    'seat-stepper',
    class SeatStepper extends QuantityInput {
      connectedCallback() {
        super.connectedCallback();
        this.productForm = document.getElementById(this.dataset.productForm);
        this.priceTarget = this.dataset.priceTarget ? document.getElementById(this.dataset.priceTarget) : null;
        this.tiers = this.readTiers();
        this.activeTier = this.matchTier(Number.parseInt(this.input.value, 10));
      }

      // Reads the per-tier lookup table rendered by main-product.liquid: an array of
      // { id, sku, min, max, priceHtml }, one entry per variant, sorted by ascending min.
      // Deliberately a separate script tag from seat-total's own data blob (data-seat-total-data)
      // so this feature never has to touch that file's contract.
      readTiers() {
        const script = this.querySelector('[data-seat-stepper-tiers]');
        if (!script) return [];
        try {
          const tiers = JSON.parse(script.textContent);
          return Array.isArray(tiers) ? tiers.slice().sort((a, b) => a.min - b.min) : [];
        } catch (error) {
          console.warn('Unable to read seat-stepper tier data', error);
          return [];
        }
      }

      // Falls back to the currently active tier (rather than null) when the quantity is outside
      // every tier's range -- e.g. a manually typed value past the union max -- so a tier is
      // never dropped over a value the checkout validation function will reject anyway. A tier
      // with no native quantity_rule.max set in Admin comes through as max: null -- treat that as
      // open-ended rather than matching nothing (which would otherwise permanently strand the
      // stepper on whichever tier loaded first).
      matchTier(quantity) {
        return (
          this.tiers.find((tier) => quantity >= tier.min && (tier.max == null || quantity <= tier.max)) ||
          this.activeTier ||
          this.tiers[0] ||
          null
        );
      }

      // Native stepUp()/stepDown() already respect min/max, but a manually TYPED value is never
      // clamped by the browser -- it just marks the field :invalid while leaving the out-of-range
      // value in place. Beyond the cap, self-serve isn't offered at all (sales-assisted only), so
      // this snaps back to the boundary rather than letting an over-cap quantity reach checkout.
      clampToBounds() {
        const min = Number.parseInt(this.input.min, 10);
        const max = Number.parseInt(this.input.max, 10);
        let value = Number.parseInt(this.input.value, 10) || 0;
        if (!Number.isNaN(min)) value = Math.max(min, value);
        if (!Number.isNaN(max)) value = Math.min(max, value);
        if (String(value) !== this.input.value) this.input.value = String(value);
      }

      onButtonClick(event) {
        super.onButtonClick(event);
        this.clampToBounds();
        this.syncTier();
      }

      onInputChange(event) {
        this.clampToBounds();
        super.onInputChange(event);
        this.syncTier();
      }

      syncTier() {
        if (!this.tiers.length || !this.productForm) return;

        const quantity = Number.parseInt(this.input.value, 10);
        const tier = this.matchTier(quantity);
        if (!tier || (this.activeTier && String(tier.id) === String(this.activeTier.id))) return;
        this.activeTier = tier;

        const idInput = this.productForm.querySelector('input[name="id"]');
        if (idInput && String(idInput.value) !== String(tier.id)) {
          idInput.value = tier.id;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }

        if (this.priceTarget && tier.priceHtml != null) {
          this.priceTarget.innerHTML = tier.priceHtml;
        }
      }
    },
  );
}
